import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { FAQ, FAQ_CATEGORIES } from "../data/content";
import { Reveal } from "./Reveal";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const FaqSection = () => {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All Questions");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQ.filter((f) => {
      const inCat = cat === "All Questions" || f.category === cat;
      const inQ = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return inCat && inQ;
    });
  }, [query, cat]);

  return (
    <section className="py-20 md:py-28 bg-offwhite" data-testid="about-faq">
      <div className="container-x">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-softgray bg-white px-4 py-1.5 label-caps text-navy">
              <Sparkles className="h-3.5 w-3.5 text-gold" /> Questions & Clear Answers
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-[1.1]">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-base md:text-lg text-charcoal/80 leading-relaxed">Clear, transparent answers for veterans, case managers, and supporting families.</p>
          </Reveal>
        </div>

        <div className="mt-12 max-w-3xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions (e.g., rules, eligibility, stay length)…"
              data-testid="faq-search"
              className="w-full rounded-full border border-softgray bg-white pl-12 pr-4 py-4 text-charcoal shadow-sm placeholder:text-slate/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/25"
            />
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {FAQ_CATEGORIES.map((c) => {
              const active = cat === c;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`faq-cat-${slug(c)}`}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                    active ? "border-navy bg-navy text-offwhite" : "border-softgray bg-white text-charcoal hover:border-navy"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="mt-8" data-testid="faq-accordion">
            {filtered.length === 0 ? (
              <p className="text-center text-charcoal/70 py-10">No questions match your search. Try a different term or category.</p>
            ) : (
              <Accordion type="single" collapsible className="w-full space-y-3">
                {filtered.map((item, i) => (
                  <AccordionItem key={item.q} value={`item-${i}`} className="i-card border-b-0 px-6">
                    <AccordionTrigger className="text-left font-heading text-base md:text-lg font-bold text-navy hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-charcoal/80 leading-relaxed text-[15px] md:text-base pb-6">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>

          <Reveal delay={0.05}>
            <div className="mt-8 i-card p-6 md:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="faq-contact-cta">
              <div>
                <h3 className="font-heading text-lg font-bold text-navy">Have a specific question not listed here?</h3>
                <p className="mt-1 text-sm text-charcoal/70">Our intake team responds within one business day.</p>
              </div>
              <Link to="/contact" className="group shrink-0 inline-flex items-center gap-2 bg-navy text-offwhite px-6 py-3.5 rounded-sm text-sm font-semibold hover:bg-navy-deep transition-colors" data-testid="faq-contact-btn">
                Contact Intake Team <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
